import { z } from "zod";

/** Labels as the provider enforces them (SPEC §7.1): key and value ≤ 63, alphanumeric ends. */
const LABEL_PART = /^([a-z0-9A-Z]([a-z0-9A-Z._-]{0,61}[a-z0-9A-Z])?)?$/;
export const Labels = z
  .record(z.string().min(1).max(63).regex(LABEL_PART), z.string().max(63).regex(LABEL_PART))
  .refine((labels) => Object.keys(labels).length <= 64, "At most 64 labels");
export type Labels = z.infer<typeof Labels>;

/**
 * An OpenSSH public key line: type, base64 body, optional comment. The
 * provider checks the key itself; this only catches pasting the wrong thing
 * (a private key, a fingerprint, a broken line).
 */
export const PUBLIC_KEY_PATTERN =
  /^(ssh-ed25519|ssh-rsa|ecdsa-sha2-nistp(256|384|521)|sk-ssh-ed25519@openssh\.com|sk-ecdsa-sha2-nistp256@openssh\.com) [A-Za-z0-9+/]+={0,3}( [^\r\n]*)?$/;

export const SshKeyName = z.string().trim().min(1).max(128);

export const SshKey = z.object({
  id: z.uuid(),
  name: z.string(),
  /** MD5 fingerprint as the provider shows it, `aa:bb:…`. */
  fingerprint: z.string(),
  publicKey: z.string(),
  labels: Labels,
  createdAt: z.iso.datetime(),
});
export type SshKey = z.infer<typeof SshKey>;

export const CreateSshKeyRequest = z.object({
  name: SshKeyName,
  publicKey: z.string().trim().max(16_384).regex(PUBLIC_KEY_PATTERN, "Not an OpenSSH public key"),
  labels: Labels.default({}),
});
export type CreateSshKeyRequest = z.infer<typeof CreateSshKeyRequest>;

export const UpdateSshKeyRequest = z
  .object({ name: SshKeyName.optional(), labels: Labels.optional() })
  .refine((v) => v.name !== undefined || v.labels !== undefined, "Nothing to change");
export type UpdateSshKeyRequest = z.infer<typeof UpdateSshKeyRequest>;
